import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, FilePlus2, LogOut, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { euro } from "@/lib/format";
import { PHONE_CODES } from "@/lib/phoneCodes";
import { PhoneWarn } from "@/components/PhoneWarn";
import { ClientTypeField, clientTypeOf } from "@/components/ClientTypeField";
import { CityInput, countryCodeOf } from "@/components/CityInput";
import { splitAddress } from "@/lib/clientAddress";

const EMPTY = { firstname: "", name: "", company: "", email: "", phone_code: "+33", phone: "", address: "", postal: "", city: "", country: "", source: "", client_type: "", siret: "" };
const REQ = ["name", "firstname", "email", "phone", "address", "postal", "city"];
const emptyReq = (form) => REQ.filter((k) => (k === "email" ? !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((form.email || "").trim())
  : k === "phone" ? (form.phone || "").replace(/\D/g, "").length < 6 : !(form[k] || "").trim()));
const FIELDS = [["name", "Nom"], ["firstname", "Prénom"], ["company", "Société"], ["email", "Email"], ["phone", "Téléphone"], ["address", "Adresse"], ["postal", "Code postal"], ["city", "Ville"], ["country", "Pays (code)"], ["source", "Provenance du contact"]];

const ClientQuotes = ({ quotes, t }) => (
  <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="client-quotes">
    {!quotes.length && <div className="text-sm text-muted-foreground">{t("Aucun devis pour ce client.")}</div>}
    {quotes.map((q) => (
      <div key={q.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`client-quote-${q.quote_number}`}>
        <span className="font-mono text-xs">{q.quote_number}</span>
        <span className="text-xs text-muted-foreground">{(q.created_at || "").slice(0, 10)}</span>
        <span className="flex-1 truncate">{q.model_name}</span>
        <span className="text-xs">{q.reseller_name}</span>
        <Badge variant={q.signed ? "default" : "outline"} className="text-[10px]">{q.signed ? t("Signé") : t(q.status || "Brouillon")}</Badge>
        <span className="font-medium">{euro(q.total_public_ttc || 0, q.currency)}</span>
        {q.can_open && <Link to={`/configurer?quote=${q.id}`} className="text-xs text-primary underline" data-testid={`client-quote-open-${q.quote_number}`}>{t("Ouvrir")}</Link>}
      </div>
    ))}
  </div>
);

export const ClientSheet = ({ clientId, onClose, onSaved, onNewQuote }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const isNew = clientId === "new";
  const [c, setC] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState("");
  const [tried, setTried] = useState(false);
  useEffect(() => {
    if (!clientId) { setC(null); return; }
    const country = (user?.sectors || [])[0]?.country || "FR";
    if (isNew) { setC({ quotes: [], owners: [] }); setForm({ ...EMPTY, country }); setInitial(JSON.stringify({ ...EMPTY, country })); return; }
    api.get(`/clients/${clientId}`).then(({ data }) => { const sp = splitAddress(data); const f = { ...EMPTY, ...data, address: sp.address, postal: sp.postal, city: sp.city };
      setC(data); setForm(f); setInitial(JSON.stringify(f)); })
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); onClose(); });
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Enregistre seulement si la fiche a changé ; renvoie la fiche à jour, ou null en cas d'erreur
  const saveIfChanged = async () => {
    if (JSON.stringify(form) === initial) return c;
    if (isNew && !Object.entries(form).some(([k, v]) => !["phone_code", "country", "created_by_id"].includes(k) && String(v || "").trim())) return c;
    const miss = emptyReq(form);
    if (miss.length) { setTried(true); toast.error(`${t("Champs obligatoires manquants")} : ${miss.map((k) => t(FIELDS.find((f) => f[0] === k)[1])).join(", ")}`); return null; }
    setSaving(true);
    try {
      const { data } = isNew ? await api.post("/clients", form) : await api.put(`/clients/${clientId}`, form);
      toast.success(t("Fiche client enregistrée")); onSaved();
      return data;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return null; } finally { setSaving(false); }
  };
  const blankNew = () => isNew && ["name", "firstname", "company", "email", "phone", "address", "postal", "city", "source", "siret"].every((k) => !(form[k] || "").trim());
  const quit = async () => { if (blankNew()) { onClose(); return; } if (await saveIfChanged()) onClose(); };
  const newQuote = async () => { const d = await saveIfChanged(); if (d) onNewQuote({ ...c, ...d }); };
  const isMgr = user?.role === "admin" || !!user?.sales_manager;
  const [resellers, setResellers] = useState([]);
  useEffect(() => {
    if (isNew && isMgr) api.get("/act-as/resellers").then(({ data }) => setResellers(data)).catch(() => {});
  }, [isNew, isMgr]);
  const [divalto, setDivalto] = useState("");
  useEffect(() => { setDivalto(c?.divalto_code || ""); }, [c?.divalto_code]);
  const saveDivalto = async () => {
    if (divalto.trim() === (c.divalto_code || "")) return;
    try {
      const { data } = await api.put(`/clients/${clientId}/divalto`, { divalto_code: divalto });
      setC((x) => ({ ...x, divalto_code: data.divalto_code })); onSaved(); toast.success(t("Code client Divalto enregistré"));
    } catch (e) { setDivalto(c.divalto_code || ""); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const canDelete = !isNew && c && (user?.role === "admin" ? !c.quotes?.some((q) => q.signed) : !c.quotes?.length && c.created_by_id === user?.id);
  const remove = async () => {
    if (!window.confirm(c.quotes?.length ? t("Supprimer définitivement cette fiche client ? Ses devis non signés seront conservés, sans fiche client.") : t("Supprimer définitivement cette fiche client ?"))) return;
    setSaving(true);
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success(t("Fiche client supprimée")); onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!clientId} onOpenChange={(o) => !o && !saving && quit()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="client-sheet">
        <DialogHeader><DialogTitle>{isNew ? t("Nouveau client") : t("Fiche client")}</DialogTitle></DialogHeader>
        {!c ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (<>
          {!isNew && (
            <div className="grid sm:grid-cols-2 gap-3 text-sm rounded-lg bg-muted/40 p-3" data-testid="client-roles">
              <div><div className="text-xs text-muted-foreground">{t("Responsable (secteur)")}</div>
                <div data-testid="client-owners">{(c.owners || []).length ? c.owners.map((o) => <Badge key={o.id} variant="outline" className="mr-1 mb-1">{o.name}{o.company ? ` · ${o.company}` : ""}{(o.ranges || []).length ? ` (${o.ranges.map((r) => t(r)).join(", ")})` : ""}</Badge>) : t("Aucun revendeur sur ce secteur")}</div></div>
              <div><div className="text-xs text-muted-foreground">{t("Créateur de la fiche")}</div>
                <div data-testid="client-creator">{c.created_by_name} <span className="text-xs text-muted-foreground">{(c.created_at || "").slice(0, 10)}</span>
                  {isMgr && c.entered_by_name && <div className="text-[10px] text-muted-foreground" data-testid="client-entered-by">{t("Saisi par")} {c.entered_by_name}</div>}</div></div>
              <div><div className="text-xs text-muted-foreground">{t("Code client Divalto")}</div>
                {isMgr ? <Input value={divalto} onChange={(e) => setDivalto(e.target.value)} onBlur={saveDivalto} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  className="mt-1 h-8 font-mono w-48" data-testid="client-divalto-code" />
                  : <div className="font-mono" data-testid="client-divalto-code">{c.divalto_code || "—"}</div>}</div>
            </div>
          )}
          {isNew && isMgr && (
            <div className="rounded-lg bg-muted/40 p-3"><Label className="text-xs">{t("Créateur de la fiche")}</Label>
              <select value={form.created_by_id || ""} onChange={(e) => setForm({ ...form, created_by_id: e.target.value })}
                className="mt-1 h-10 w-full sm:w-96 rounded-md border border-input bg-background px-2 text-sm block" data-testid="client-creator-select">
                <option value="">{t("Moi-même")}</option>
                {resellers.map((r) => <option key={r.id} value={r.id}>{r.name}{r.company ? ` · ${r.company}` : ""}</option>)}
              </select>
            </div>
          )}
          <div className="grid sm:grid-cols-3 gap-3">
            <ClientTypeField client={form} t={t} testid="client-field" hideSiret onChange={(p) => setForm((f) => ({ ...f, ...p }))} />
            {FIELDS.map(([k, l]) => (
              <div key={k} className={k === "address" || k === "source" ? "sm:col-span-3" : ""}><Label className="text-xs">{t(l)}{REQ.includes(k) && " *"}</Label>
                {k === "phone" ? (<>
                  <div className="mt-1 flex gap-2">
                    <select value={form.phone_code || "+33"} onChange={(e) => setForm({ ...form, phone_code: e.target.value })} data-testid="client-field-phone_code" className="h-10 rounded-md border border-input bg-background px-2 text-sm w-24 shrink-0">
                      {PHONE_CODES.map((p) => <option key={p.c} value={p.c}>{p.l} {p.c}</option>)}
                    </select>
                    <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={tried && emptyReq(form).includes("phone") ? "border-destructive ring-1 ring-destructive" : ""} data-testid="client-field-phone" />
                  </div>
                  <PhoneWarn phone={form.phone} code={form.phone_code} testid="client-field-phone-warn" />
                </>) : k === "city" ? (
                  <CityInput value={form.city || ""} onChange={(v) => setForm((f) => ({ ...f, city: v }))} postal={form.postal} countryCode={countryCodeOf(form.country, localStorage.getItem("fautras_country") || "FR")}
                    testid="client-field-city" className={`mt-1 ${tried && emptyReq(form).includes("city") ? "border-destructive ring-1 ring-destructive" : ""}`} />
                ) : <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value, ...(k === "company" && e.target.value.trim() ? { client_type: "societe" } : {}) })} className={`mt-1 ${tried && emptyReq(form).includes(k) ? "border-destructive ring-1 ring-destructive" : ""}`} data-testid={`client-field-${k}`} />}
                {k === "company" && clientTypeOf(form) === "societe" && (<>
                  <Label className="text-xs mt-2 block">{t("SIRET")}</Label>
                  <Input value={form.siret || ""} inputMode="numeric" maxLength={17} onChange={(e) => setForm({ ...form, siret: e.target.value })} className="mt-1" data-testid="client-field-siret" />
                </>)}</div>
            ))}
          </div>
          {!isNew && <div><div className="text-sm font-semibold mb-2">{t("Devis")} ({c.quotes.length})</div><ClientQuotes quotes={c.quotes} t={t} /></div>}
          <DialogFooter className="gap-2">
            {canDelete && <Button variant="ghost" className="gap-1 text-destructive sm:mr-auto" onClick={remove} disabled={saving} data-testid="client-delete"><Trash2 className="w-4 h-4" /> {t("Supprimer la fiche")}</Button>}
            {!isNew && <Button variant="outline" className="gap-1" onClick={newQuote} disabled={saving} data-testid="client-new-quote"><FilePlus2 className="w-4 h-4" /> {t("Nouveau devis")}</Button>}
            <Button className="gap-1" onClick={quit} disabled={saving} data-testid="client-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />} {t("Quitter")}</Button>
          </DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
};
